import MainButton from '@/components/ui/custom/buttons/MainButton';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import CkEditorInput from '@/components/ui/custom/dataInput/CkEditorInput';
import { BlockCodeActivityEditElement } from '@/models/_deprecated/ActivityElement';
import { ImageFolderEnum } from '@/models/CkEditor';
import { ApiError } from '@/models/Errors';
import { ArrowDownOnSquareIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BlockCodeServiceProps } from './BlockCodeService';
import WorkspacesForm from './WorkspacesForm';
import TextAreaInput from '@/components/ui/custom/dataInput/TextAreaInput';
import ValidateButton from '@/components/ui/custom/buttons/ValidateButton';
export type CodeFields = Pick<
  BlockCodeActivityEditElement,
  | 'title'
  | 'instructions'
  | 'tip'
  | 'maxBlocks'
  | 'solutionCode'
  | 'solutionWorkspace'
  | 'initialCode'
  | 'initialWorkspace'
  | 'expectedOutput'
>;

export type BlockFieldsKeys = keyof CodeFields;

type BlockCodeFormProps = Omit<BlockCodeServiceProps, 'activity'> & {
  error?: ApiError<BlockFieldsKeys>;
  onSave: (codeActivity: CodeFields) => void;
  isSaving?: boolean;
  onValidate?: (codeActivity: CodeFields) => void;
  isValidating?: boolean;
  validationPassed?: boolean | null;
};

export default function BlockCodeForm({
  question,
  onEdit,
  onSave,
  error,
  isSaving,
  onValidate,
  isValidating,
  validationPassed,
}: BlockCodeFormProps) {
  const {
    solutionWorkspace: solutionJson,
    initialWorkspace: initialJson,
    changeStatus,
    ...codeQuestion
  } = question;

  const { t } = useTranslation('translation', {
    keyPrefix: 'activity.manageActivity.activityBlock',
  });

  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    watch,
    setValue,
    register,
    handleSubmit,
    control,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<CodeFields>({
    defaultValues: {
      ...codeQuestion,
      initialWorkspace: initialJson,
      solutionWorkspace: solutionJson,
    },
    shouldFocusError: true,
  });

  const { field } = useController({
    control,
    name: 'instructions',
    rules: {
      required: t('instructions.required'),
    },
  });

  useEffect(() => {
    const w = watch(e => onEdit({ ...e, changeStatus, id: codeQuestion.id }));
    return () => w.unsubscribe();
  }, [changeStatus, codeQuestion, onEdit, watch]);

  useEffect(() => {
    for (const errorField of error?.fields || []) {
      setError(errorField, {
        message: error?.getMessageByField(errorField),
      });
    }
  }, [error, setError]);
  const hasErrors = Object.keys(errors).length > 0;

  const onSaveWrapper = (data: CodeFields) => onSave(data);
  return (
    <form
      onSubmit={handleSubmit(onSaveWrapper)}
      className="flex flex-col gap-7 overflow-hidden w-full"
    >
      <TextInput
        register={register('title', {
          required: t('title.required'),
        })}
        label={t('title.label')}
        id="title"
        errorLabelText={errors.title?.message}
        errorLabelPosition="bottom"
      />
      <CkEditorInput
        label={t('instructions.label')}
        error={errors.instructions?.message}
        value={field.value}
        onChange={field.onChange}
        folder={ImageFolderEnum.ACTIVITY}
        testId="instructions"
      />

      <WorkspacesForm
        control={control}
        setValue={setValue}
        clearErrors={clearErrors}
        solutionJson={solutionJson}
        initialJson={initialJson}
      />

      <TextAreaInput
        register={register('expectedOutput', {
          required: t('expectedOutput.required'),
          onChange: () => clearErrors('solutionCode'),
        })}
        label={t('expectedOutput.label')}
        id="expectedOutput"
        className={{
          label: 'text-sm',
        }}
        errorLabelText={errors.expectedOutput?.message}
        errorLabelPosition="bottom"
      />
      <section className="flex gap-3 items-center">
        <MainButton
          type="submit"
          icon={<ArrowDownOnSquareIcon />}
          text={tCommon('save')}
          disabled={changeStatus === 'edit' || !changeStatus || hasErrors}
          loading={isSaving}
        />
        <ValidateButton
          control={control}
          onValidate={onValidate}
          validation={validationPassed}
          validationFields={['solutionCode', 'expectedOutput']}
          isValidating={isValidating}
        />
      </section>
    </form>
  );
}
